import React from "react";
import BottomSection from "../components/BottomSection";
import Header from "../components/Header";

const TermsOfService = () => {
  return (
    <div>
      <Header />
      <div className='max-w-3xl mx-auto my-10 px-6'>
        <h1 className='text-3xl font-bold text-center text-gray-800'>
          Terms of Service
        </h1>
        <p className='text-gray-600 mt-4'>
          <b>Effective Date:</b> July 20, 2026
          <br />
          <b>Last Updated:</b> July 20, 2026
        </p>
        <p className='text-gray-600 mt-4'>
          These Terms of Service ("Terms") govern your access to and use of the
          HomeOwner mobile application, website, and related services
          (collectively, the "Service"), owned and operated by Arptix Solutions
          Inc. ("Arptix", "we", "us", or "our").
        </p>
        <p className='text-gray-600 mt-4'>
          By creating an account or using the Service, you agree to these Terms.
          If you do not agree, do not use the Service.
        </p>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            1. Eligibility
          </h2>
          <p className='text-gray-600 mt-2'>
            You must be at least 18 years of age to create an account and use
            the Service. This requirement is based on legal capacity, not
            content: using HomeOwner means entering into a binding purchase
            agreement and handling personal, financial, and tenant information,
            which requires you to be an adult able to enter into a contract. By
            using the Service, you represent that you are at least 18 and
            legally able to agree to these Terms.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            2. The Service
          </h2>
          <p className='text-gray-600 mt-2'>
            HomeOwner is a tool that helps self-managing landlords track rent,
            log expenses, manage tenant and property records, set reminders,
            generate income and expense summaries and year-end tax reports, and
            store related documents. The Service is provided to assist you with
            your own record-keeping and property management.
          </p>
          <p className='text-gray-600 mt-2'>
            HomeOwner is not a law firm, accounting firm, credit reporting
            agency, or financial advisor. Any guidance, template, checklist,
            summary, or report provided through the Service is for informational
            purposes only and is not legal, tax, accounting, or financial
            advice. You are responsible for verifying information and, where
            appropriate, consulting a qualified professional.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            3. Free Trial
          </h2>
          <p className='text-gray-600 mt-2'>
            New accounts include a free trial period, currently 30 days, during
            which you may use the Service without payment and without providing
            a payment method.
          </p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              The free trial does not automatically convert into a charge.
              Nothing is billed automatically.
            </li>
            <li>
              When the trial period ends, you must make the one-time purchase
              described below to continue using the paid features. If you do
              not, your access is locked, but your data is preserved (see
              Section 6).
            </li>
            <li>
              We may change the length of the free trial for future users. Any
              change does not affect a trial already in progress.
            </li>
          </ul>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            4. Purchases, Pricing & Access
          </h2>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>4.1 One-Time Purchase</h3>
            <p className='text-gray-600 mt-2'>
              Access to the Service after the free trial requires a one-time
              purchase (the "Unlock"). The current introductory price is
              displayed in the app at the time of purchase.
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                The Unlock is a one-time purchase, not a subscription. There is
                no recurring or automatic billing.
              </li>
              <li>
                The Unlock grants you access to the paid features that exist at
                the time of purchase, for as long as the Service remains
                operational.
              </li>
              <li>
                We may change the price of the Unlock for future purchasers. A
                price change does not re-charge or affect users who have already
                purchased.
              </li>
            </ul>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>4.2 In-App Purchases</h3>
            <p className='text-gray-600 mt-2'>
              Certain features (for example, downloadable year-end tax reports,
              and other features we may introduce) are available as separate
              optional in-app purchases at the prices shown in the app. Features
              introduced after your original purchase may require an additional
              purchase.
            </p>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>4.3 Payment Processing</h3>
            <p className='text-gray-600 mt-2'>
              All payments are processed by the Apple App Store or Google Play,
              not by Arptix. We do not collect or store your payment card or
              bank details. Your purchase is also subject to the applicable app
              store's terms and policies.
            </p>
          </div>

          <div className='ml-5 mt-4'>
            <h3 className='text-xl font-semibold'>4.4 Refunds</h3>
            <p className='text-gray-600 mt-2'>
              Because you can try the Service free for 30 days before paying,
              purchases are made after you have had the opportunity to evaluate
              the Service.
            </p>
            <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
              <li>
                Refunds for purchases are processed by the Apple App Store or
                Google Play under their own policies, and we do not control
                their decisions.
              </li>
              <li>
                If something is not working for you, you may contact us at
                info@thehomeowner.ca and we will try to help.
              </li>
            </ul>
          </div>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            5. Your Account
          </h2>
          <p className='text-gray-600 mt-2'>
            You are responsible for maintaining the confidentiality of your
            login credentials and for all activity under your account. Notify us
            promptly of any unauthorized use. You agree to provide accurate
            information and to keep it up to date.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            6. Your Data and Access
          </h2>
          <p className='text-gray-600 mt-2'>
            Your data belongs to you. If your free trial ends or your access is
            otherwise locked pending purchase, we do not delete your data. Your
            information is preserved and becomes available again when you unlock
            the Service.
          </p>
          <p className='text-gray-600 mt-2'>
            You may delete your account or individual records at any time
            through the app. Deleting your account permanently erases your
            personal and property data, including your profile, properties,
            tenant records, documents, and expense records. This deletion is
            irreversible.
          </p>
          <p className='text-gray-600 mt-2'>
            We retain only a limited financial record of any purchases you made,
            stripped of your personal details, where tax, accounting, or
            consumer protection laws require us to keep records of completed
            sales, or to handle a later refund or dispute. Our handling of your
            personal information is described in our Privacy Policy.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            7. Landlord and Tenant Responsibilities
          </h2>
          <p className='text-gray-600 mt-2'>
            If you enter a tenant's personal information into the Service, you
            represent and warrant that you have obtained that tenant's
            meaningful, informed consent to do so, as further described in our
            Privacy Policy. You are solely responsible for how you use the
            Service in your dealings with tenants, including compliance with
            applicable residential tenancy, human rights, consumer reporting,
            and privacy laws.
          </p>
          <p className='text-gray-600 mt-2'>
            The legal information, templates, and guidance in the Service are
            general and may not reflect your specific circumstances or the
            current law in your jurisdiction. You are responsible for ensuring
            any notice, form, or action you take is correct and lawful.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            8. Accuracy of Information and Reliance on Your Input
          </h2>
          <p className='text-gray-600 mt-2'>
            The Service produces summaries, reports, reminders, calculations,
            and other outputs based entirely on the information you enter. We do
            not independently verify what you provide. The accuracy and
            usefulness of any output, including expense summaries and year-end
            tax reports, depends entirely on the accuracy and completeness of
            your inputs.
          </p>
          <p className='text-gray-600 mt-2'>
            You are responsible for entering correct information and for
            reviewing any output before relying on it or sharing it with a third
            party, such as an accountant or a tribunal. We are not responsible
            for any consequence arising from inaccurate, incomplete, or outdated
            information you provide.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            9. Acceptable Use
          </h2>
          <p className='text-gray-600 mt-2'>You agree not to:</p>
          <ul className='list-disc text-gray-600 mt-2 ml-5 space-y-2'>
            <li>
              Use the Service for any unlawful purpose or in violation of any
              applicable law.
            </li>
            <li>
              Upload content you do not have the right to upload, or that
              infringes the rights of others.
            </li>
            <li>
              Attempt to gain unauthorized access to the Service, other
              accounts, or our systems.
            </li>
            <li>
              Interfere with or disrupt the integrity or performance of the
              Service.
            </li>
            <li>
              Reverse engineer, resell, or attempt to circumvent the paid
              features of the Service.
            </li>
          </ul>
          <p className='text-gray-600 mt-2'>
            We may suspend or terminate accounts that violate these Terms,
            including where a landlord has not obtained required tenant consent.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            10. Intellectual Property
          </h2>
          <p className='text-gray-600 mt-2'>
            The Service, including its software, design, and content (excluding
            your own data and uploaded content), is owned by Arptix and
            protected by intellectual property laws. We grant you a limited,
            non-exclusive, non-transferable, revocable license to use the
            Service for its intended purpose. You retain ownership of the data
            and documents you provide.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            11. Third-Party Services and Links
          </h2>
          <p className='text-gray-600 mt-2'>
            The Service may reference or link to third-party websites, services,
            or providers (for example, recommended local services). Those third
            parties control their own websites and content. We do not own,
            operate, endorse, or control that content, and we are not
            responsible for it or for the products, services, or practices of
            those third parties. Any dealings you have with them, and any
            information you provide to them, are solely between you and that
            third party and are governed by their own terms and privacy
            policies.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            12. Disclaimers
          </h2>
          <p className='text-gray-600 mt-2'>
            The Service is provided "as is" and "as available", without
            warranties of any kind, whether express or implied, including
            warranties of merchantability, fitness for a particular purpose, and
            non-infringement. We do not warrant that the Service will be
            uninterrupted, error-free, or that any information, summary, or
            report it produces is accurate or suitable for your purposes.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            13. Limitation of Liability
          </h2>
          <p className='text-gray-600 mt-2'>
            To the maximum extent permitted by law, Arptix and its officers,
            directors, employees, and agents will not be liable for any
            indirect, incidental, special, consequential, or punitive damages,
            or any loss of profits, data, or goodwill, arising from your use of
            or inability to use the Service.
          </p>
          <p className='text-gray-600 mt-2'>
            To the maximum extent permitted by law, our total liability for any
            claim arising out of or relating to the Service will not exceed the
            amount you paid us for the Service in the twelve months preceding
            the claim.
          </p>
          <p className='text-gray-600 mt-2'>
            Nothing in these Terms limits liability that cannot be limited under
            applicable law.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            14. Termination
          </h2>
          <p className='text-gray-600 mt-2'>
            You may stop using the Service and delete your account at any time.
            We may suspend or terminate your access if you violate these Terms
            or use the Service in a way that could cause harm or legal
            liability. On termination, the license granted to you ends.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            15. Changes to These Terms
          </h2>
          <p className='text-gray-600 mt-2'>
            We may update these Terms from time to time. The updated Terms will
            be available within the app and on our website, and the "Last
            Updated" date will be revised. For material changes, we will provide
            reasonable notice. Continued use of the Service after changes take
            effect means you accept the revised Terms.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            16. Governing Law
          </h2>
          <p className='text-gray-600 mt-2'>
            These Terms are governed by the laws of the Province of Ontario and
            the federal laws of Canada applicable therein, without regard to
            conflict-of-law principles. You agree to the exclusive jurisdiction
            of the courts located in Ontario for any dispute arising out of or
            relating to these Terms or the Service, subject to any mandatory
            consumer protections available to you under the law of your province
            of residence.
          </p>
        </section>

        <section className='mt-8'>
          <h2 className='text-2xl font-semibold text-gray-800'>
            17. Contact Us
          </h2>
          <p className='text-gray-600 mt-2'>
            If you have questions about these Terms, contact us at:
          </p>
          <p className='text-gray-600 mt-2'>
            <b>Email:</b>{" "}
            <a
              href='mailto:info@thehomeowner.ca'
              className='text-blue-600 hover:underline'>
              info@thehomeowner.ca
            </a>
            <br />
            Arptix Solutions Inc.
          </p>
        </section>
      </div>
      <BottomSection />
    </div>
  );
};

export default TermsOfService;
